// Search term transport: sessionStorage survives the full-document round trip that destroys React state.

const STORAGE_KEY_PREFIX = 'everfi.library.search';

export const URL_SEARCH_PARAM = 'search';

const storageKey = programId => `${STORAGE_KEY_PREFIX}.${programId}`;

// Fallback store; lasts this page load only, so it degrades the round trip rather than breaking it.
const memoryFallback = new Map();

// sessionStorage throws in Safari private mode and partitioned frames; probe once, not on every access.
const sessionStore = (() => {
  try {
    const probeKey = `${STORAGE_KEY_PREFIX}.probe`;
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
})();

export const readSearch = programId => {
  const key = storageKey(programId);

  if (sessionStore) {
    try {
      return sessionStore.getItem(key);
    } catch (e) {
      // fall through to the in-memory copy
    }
  }

  return memoryFallback.get(key) || null;
};

export const writeSearch = (programId, value) => {
  const key = storageKey(programId);
  const term = typeof value === 'string' ? value.trim() : '';

  if (sessionStore) {
    try {
      if (term) {
        sessionStore.setItem(key, term);
      } else {
        sessionStore.removeItem(key);
      }
      return;
    } catch (e) {
      // quota or partitioned frame - keep it in memory instead
    }
  }

  if (term) {
    memoryFallback.set(key, term);
  } else {
    memoryFallback.delete(key);
  }
};

// Legacy ?search= link: honoured once then stripped. Returns the trimmed term, '' if empty, undefined if absent.
export const consumeSearchParamFromUrl = () => {
  const params = new URLSearchParams(window.location.search);

  if (!params.has(URL_SEARCH_PARAM)) return undefined;

  const term = (params.get(URL_SEARCH_PARAM) || '').trim();

  // delete() drops every occurrence, so a repeated param cannot survive
  params.delete(URL_SEARCH_PARAM);

  const url = new URL(window.location.href);
  url.search = params.toString();
  window.history.replaceState(null, '', url.toString());

  return term;
};
